'use client';

import React, { useState, useRef, useEffect, FormEvent, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import { SendHorizonal, MessageCircle, X, Loader2, Bot, User, MoreVertical, ChevronDown, Sparkles, Trash2, Clock, Minimize, PhoneForwarded, SparklesIcon } from 'lucide-react'; // Removed duplicate/unused ExternalLink here if not needed elsewhere
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ChatMessage } from './ChatMessage'; // Import the new component
import { SuggestionButton } from './SuggestionButton'; // Changed back to named import

// --- Types ---
interface Message {
  role: 'user' | 'bot';
  text: string;
  followUpQuestions?: string[];
  showContactButton?: boolean;
}
interface CachedAnswer {
  answer: string;
  followUpQuestions?: string[];
  showContactButton?: boolean;
}
const questionAnswerCache: Record<string, CachedAnswer> = {};
interface SavedChat {
    id: string;
    date: string;
    preview: string;
    messages: Message[];
}
interface PastChatDisplayItem {
    id: string;
    date: string;
    preview: string;
}

// --- Suggestion Button Component (Define it here for self-containment or ensure it's imported correctly) ---
// Assuming SuggestionButton is defined in its own file and imported above.

// --- Constants ---
const QOPILOT_CHAT_MESSAGES_KEY = 'qopilot_chat_messages_v1';
const QOPILOT_CHAT_OPEN_STATE_KEY = 'qopilot_chat_open_state_v1';
const QOPILOT_WELCOMED_KEY = 'qopilot_welcomed_v1';
const QOPILOT_SAVED_CHATS_KEY = 'qopilot_saved_chats_v1';
const MAX_SAVED_CHATS = 10;

// --- Component ---
const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentFollowUps, setCurrentFollowUps] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [showWelcomeBubble, setShowWelcomeBubble] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showPastChats, setShowPastChats] = useState(false);
  const [pastChats, setPastChats] = useState<PastChatDisplayItem[]>([]);
  const [showWelcomeImage, setShowWelcomeImage] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
  const optionsMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Effect to log isOpen changes (Keep commented out unless debugging)
  // useEffect(() => {
  //   console.log('isOpen state changed to:', isOpen);
  // }, [isOpen]);

  const initialStaticSuggestions = useMemo(() => [
    "What can you help me with?",
    "Tell me about Qogent Global.",
    "How do I get started studying abroad?",
    "What are popular study destinations?",
  ], []);

  // Effect to mark as hydrated (runs only on client)
  useEffect(() => {
    setHydrated(true);
  }, []);

  // Load state from localStorage on mount, only after hydration
  useEffect(() => {
    if (!hydrated) return;

    // Load isOpen state ONCE on hydration
    try {
      const storedOpenState = localStorage.getItem(QOPILOT_CHAT_OPEN_STATE_KEY);
      if (storedOpenState === 'true') {
        setIsOpen(true);
      }
    } catch (error) {
      console.error("Error loading open state from localStorage:", error);
      localStorage.removeItem(QOPILOT_CHAT_OPEN_STATE_KEY);
    }

    // Load messages from localStorage
    let loadedMessages = false;
    let messagesFromStorage: Message[] | null = null;
    try {
      const storedMessagesJSON = localStorage.getItem(QOPILOT_CHAT_MESSAGES_KEY);
      if (storedMessagesJSON) {
        const parsedMessages: Message[] = JSON.parse(storedMessagesJSON);
        if (Array.isArray(parsedMessages) && parsedMessages.length > 0) {
          messagesFromStorage = parsedMessages;
          setMessages(parsedMessages);
          loadedMessages = true;
          const lastMessage = parsedMessages[parsedMessages.length - 1];
          if (lastMessage?.role === 'bot' && lastMessage.followUpQuestions) {
            setCurrentFollowUps(lastMessage.followUpQuestions);
          } else {
            setCurrentFollowUps([]);
          }
        }
      }
    } catch (error) {
      console.error("Error loading messages from localStorage:", error);
      localStorage.removeItem(QOPILOT_CHAT_MESSAGES_KEY);
    }

    // Determine if welcome image should show based on loaded messages
    setShowWelcomeImage(!messagesFromStorage || messagesFromStorage.length <= 1);

    // Welcome bubble logic
    const wasPreviouslyOpen = localStorage.getItem(QOPILOT_CHAT_OPEN_STATE_KEY) === 'true';
    const hasBeenWelcomed = localStorage.getItem(QOPILOT_WELCOMED_KEY) === 'true';
    if (!hasBeenWelcomed && !wasPreviouslyOpen) {
        setShowWelcomeBubble(true);
    }

  }, [hydrated]);

  // Effect for setting initial message if chat is open and empty, and for prefetching initial suggestions.
  useEffect(() => {
    if (!hydrated || !isOpen || messages.length > 0) return;

    const initialBotMessage: Message = {
        role: 'bot',
        text: "Hi there! 👋 I'm QoPilot, your study abroad assistant. How can I help you today?",
        followUpQuestions: initialStaticSuggestions,
    };
    setMessages([initialBotMessage]);
    setCurrentFollowUps(initialBotMessage.followUpQuestions || []);
    setShowWelcomeImage(true); // Ensure welcome image shows when chat is reset or started fresh
    if (initialStaticSuggestions.length > 0) {
        prefetchSuggestedAnswers(initialStaticSuggestions.filter(q => !questionAnswerCache[q]));
    }
  }, [isOpen, messages.length, hydrated, initialStaticSuggestions]);

  // Hide welcome image once conversation starts
  useEffect(() => {
      if (messages.length > 1) {
          setShowWelcomeImage(false);
      }
      // Keep welcome image if chat is cleared (messages.length becomes 1)
      else if (messages.length === 1) {
          setShowWelcomeImage(true);
      }
  }, [messages.length]);

  // Save messages to localStorage
  useEffect(() => {
    if (!hydrated) return;
    if (messages.length === 0) {
        if (localStorage.getItem(QOPILOT_CHAT_MESSAGES_KEY)) {
            localStorage.removeItem(QOPILOT_CHAT_MESSAGES_KEY);
        }
        return;
    }
    try {
      // Avoid saving if only the initial bot message exists and it hasn't changed (simple check)
      if (messages.length === 1 && messages[0].text.startsWith("Hi there!")) {
          // Optionally, could check if localStorage already has this exact initial state
          // For simplicity, we might allow saving the initial state once.
          // Or prevent saving until user interacts.
          // Let's allow saving for now.
      }
      localStorage.setItem(QOPILOT_CHAT_MESSAGES_KEY, JSON.stringify(messages));
    } catch (error) {
      console.error("Error saving messages to localStorage:", error);
    }
  }, [messages, hydrated]);

  // Save open state to localStorage
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(QOPILOT_CHAT_OPEN_STATE_KEY, JSON.stringify(isOpen));
      if (isOpen && showWelcomeBubble) {
        setShowWelcomeBubble(false);
        localStorage.setItem(QOPILOT_WELCOMED_KEY, 'true');
      }
    } catch (error) {
      console.error("Error saving open state to localStorage:", error);
    }
  }, [isOpen, hydrated, showWelcomeBubble]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // --- Core Action Handlers ---
  const handleSuggestionClick = (suggestionText: string) => {
    if (isLoading) return;
    const userMessage: Message = { role: 'user', text: suggestionText };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setCurrentFollowUps([]);
    setInput('');

    if (questionAnswerCache[suggestionText]) {
      const cachedData = questionAnswerCache[suggestionText];
      const botMessage: Message = {
        role: 'bot',
        text: cachedData.answer,
        followUpQuestions: cachedData.followUpQuestions,
        showContactButton: cachedData.showContactButton,
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);
      setCurrentFollowUps(cachedData.followUpQuestions || []);
      playNotificationSound();
      inputRef.current?.focus();
      // DO NOT submit again if the answer was found in the cache.
      // The cache is meant to provide the answer directly.
      // If there's a need to refresh or update, that should be a separate logic.
      // submitMessageToServer(suggestionText, [...messages, userMessage], true); // REMOVED THIS LINE
      
      // If the cached answer has follow-up questions, prefetch them if they are not already cached.
      if (cachedData.followUpQuestions && cachedData.followUpQuestions.length > 0) {
        prefetchSuggestedAnswers(cachedData.followUpQuestions.filter((q: string) => !questionAnswerCache[q]));
      }
    } else {
      submitMessageToServer(suggestionText, [...messages, userMessage]);
    }
  };

  const submitMessageToServer = async (questionText: string, currentHistory: Message[], isBackgroundPrefetchUpdate = false) => {
    if (!isBackgroundPrefetchUpdate) {
        setIsLoading(true);
    }
    let successfullyGotBotResponse = false;
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: questionText,
          history: currentHistory.map(msg => ({ role: msg.role === 'bot' ? 'assistant' : 'user', content: msg.text }))
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get response from server');
      }
      const data = await response.json();
      const botMessage: Message = {
        role: 'bot',
        text: data.answer,
        followUpQuestions: data.followUpQuestions,
        showContactButton: data.showContactButton,
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);
      setCurrentFollowUps(data.followUpQuestions || []);
      successfullyGotBotResponse = true;
      if (data.followUpQuestions && data.followUpQuestions.length > 0) {
        prefetchSuggestedAnswers(data.followUpQuestions.filter((q: string) => !questionAnswerCache[q]));
      }
    } catch (error) {
      console.error('Chat submission error:', error);
      const errorMessageText = error instanceof Error ? error.message : 'Sorry, something went wrong.';
      const errorMessage: Message = { role: 'bot', text: errorMessageText };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
      setCurrentFollowUps([]);
    } finally {
      if (!isBackgroundPrefetchUpdate) {
        setIsLoading(false);
      }
      inputRef.current?.focus();
      if (successfullyGotBotResponse && !isBackgroundPrefetchUpdate) {
        playNotificationSound();
      }
    }
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;
    if (showWelcomeBubble) {
        setShowWelcomeBubble(false);
        localStorage.setItem(QOPILOT_WELCOMED_KEY, 'true');
    }
    const userMessage: Message = { role: 'user', text: trimmedInput };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setCurrentFollowUps([]);
    setInput('');
    await submitMessageToServer(trimmedInput, [...messages, userMessage]);
  };

  // --- Audio & Sound --- 
  useEffect(() => {
    if (!hydrated || !isOpen) return;
    const initializeAudio = () => {
      if (!notificationSoundRef.current) {
        notificationSoundRef.current = new Audio('/chat-notification.mp3');
        notificationSoundRef.current.volume = 0.5;
        notificationSoundRef.current.preload = 'auto';
      }
    };
    const initializeAudioOnInteraction = () => {
      initializeAudio();
      document.removeEventListener('click', initializeAudioOnInteraction, true);
      document.removeEventListener('keydown', initializeAudioOnInteraction, true);
    };
    if (!notificationSoundRef.current) {
        initializeAudio();
        document.addEventListener('click', initializeAudioOnInteraction, { once: true, capture: true });
        document.addEventListener('keydown', initializeAudioOnInteraction, { once: true, capture: true });
    }
    return () => {
      document.removeEventListener('click', initializeAudioOnInteraction, true);
      document.removeEventListener('keydown', initializeAudioOnInteraction, true);
    };
  }, [hydrated, isOpen]);

  const playNotificationSound = () => {
    if (notificationSoundRef.current) {
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current.play().catch(err => {
        console.warn("Error playing notification sound:", err);
      });
    }
  };

  const handleContactRedirect = () => {
    setIsOpen(false);
    window.location.href = '/contact';
  };

  // --- Options Menu, Clear Chat, Past Chats --- 
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleClearChat = () => {
    setShowOptionsMenu(false);
    setShowClearConfirmation(true);
  };

  const confirmClearChat = () => {
    const initialBotMessage: Message = {
      role: 'bot',
      text: "Hi there! 👋 I'm QoPilot, your study abroad assistant. How can I help you today?",
      followUpQuestions: initialStaticSuggestions,
    };
    setMessages([initialBotMessage]);
    setCurrentFollowUps(initialStaticSuggestions);
    if (hydrated) {
      localStorage.removeItem(QOPILOT_CHAT_MESSAGES_KEY);
    }
    setShowClearConfirmation(false);
    setShowWelcomeImage(true);
    setTimeout(scrollToBottom, 0);
    if (initialStaticSuggestions.length > 0) {
        prefetchSuggestedAnswers(initialStaticSuggestions.filter(q => !questionAnswerCache[q]));
    }
  };

  const cancelClearChat = () => {
    setShowClearConfirmation(false);
  };

  const saveCurrentChat = () => {
    if (!hydrated || messages.length <= 1) return null;
    try {
      const savedChatsJSON = localStorage.getItem(QOPILOT_SAVED_CHATS_KEY);
      let allSavedChats: SavedChat[] = savedChatsJSON ? JSON.parse(savedChatsJSON) : [];
      const firstUserMessage = messages.find(msg => msg.role === 'user');
      const preview = firstUserMessage ? firstUserMessage.text.substring(0, 50) + (firstUserMessage.text.length > 50 ? '...' : '') : 'Chat Session';
      const newChat: SavedChat = {
        id: `chat_${Date.now()}`,
        date: new Date().toLocaleDateString(),
        preview,
        messages: [...messages],
      };
      allSavedChats.unshift(newChat);
      if (allSavedChats.length > MAX_SAVED_CHATS) {
        allSavedChats = allSavedChats.slice(0, MAX_SAVED_CHATS);
      }
      localStorage.setItem(QOPILOT_SAVED_CHATS_KEY, JSON.stringify(allSavedChats));
      return newChat.id;
    } catch (error) {
      console.error("Error saving current chat:", error);
      return null;
    }
  };

  const loadSavedChat = (chatId: string) => {
    if (!hydrated) return;
    try {
      const savedChatsJSON = localStorage.getItem(QOPILOT_SAVED_CHATS_KEY);
      if (!savedChatsJSON) return;
      const allSavedChats: SavedChat[] = JSON.parse(savedChatsJSON);
      const chatToLoad = allSavedChats.find(c => c.id === chatId);
      if (chatToLoad) {
        setMessages(chatToLoad.messages);
        const lastMessage = chatToLoad.messages[chatToLoad.messages.length - 1];
        if (lastMessage?.role === 'bot' && lastMessage.followUpQuestions) {
          setCurrentFollowUps(lastMessage.followUpQuestions);
        } else if (chatToLoad.messages.length === 1 && chatToLoad.messages[0]?.role === 'bot') {
          setCurrentFollowUps(initialStaticSuggestions);
        } else {
          setCurrentFollowUps([]);
        }
        setShowPastChats(false);
        setTimeout(scrollToBottom, 0);
        if (lastMessage?.role === 'bot' && lastMessage.followUpQuestions && lastMessage.followUpQuestions.length > 0) {
          prefetchSuggestedAnswers(lastMessage.followUpQuestions.filter((q: string) => !questionAnswerCache[q]));
        }
      }
    } catch (error) {
      console.error("Error loading saved chat:", error);
    }
  };

  const deleteSavedChat = (chatId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!hydrated) return;
    try {
      const savedChatsJSON = localStorage.getItem(QOPILOT_SAVED_CHATS_KEY);
      if (!savedChatsJSON) return;
      let allSavedChats: SavedChat[] = JSON.parse(savedChatsJSON);
      allSavedChats = allSavedChats.filter(c => c.id !== chatId);
      localStorage.setItem(QOPILOT_SAVED_CHATS_KEY, JSON.stringify(allSavedChats));
      setPastChats(allSavedChats.map(chat => ({ id: chat.id, date: chat.date, preview: chat.preview })));
    } catch (error) {
      console.error("Error deleting saved chat:", error);
    }
  };

  const handleViewPastChats = () => {
    saveCurrentChat();
    try {
      const savedChatsJSON = localStorage.getItem(QOPILOT_SAVED_CHATS_KEY);
      if (savedChatsJSON) {
        const allSavedChats: SavedChat[] = JSON.parse(savedChatsJSON);
        setPastChats(allSavedChats.map(chat => ({ id: chat.id, date: chat.date, preview: chat.preview })));
      } else {
        setPastChats([]);
      }
    } catch (error) {
      console.error("Error loading past chats for display:", error);
      setPastChats([]);
    }
    setShowOptionsMenu(false);
    setShowPastChats(true);
  };
  
  // Auto-save chat when widget is closed if there's history
  useEffect(() => {
    if (!isOpen && hydrated && messages.length > 1) {
      saveCurrentChat();
    }
  }, [isOpen, hydrated, messages]);

  // --- Prefetching --- 
  const prefetchSuggestedAnswers = async (questions: string[]) => {
    if (!hydrated) return;
    questions.forEach(async (question) => {
      if (questionAnswerCache[question]) return;
      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question, history: [] }),
        });
        if (response.ok) {
          const data = await response.json();
          questionAnswerCache[question] = {
            answer: data.answer,
            followUpQuestions: data.followUpQuestions || [],
            showContactButton: data.showContactButton || false,
          };
        } 
      } catch (error) { /* Ignore prefetch errors */ }
    });
  };

  // --- Render --- 
  return (
    <>
      {/* Welcome Bubble */}
      <AnimatePresence>
        {hydrated && showWelcomeBubble && !isOpen && (
          <motion.div
            className="fixed bottom-24 right-6 z-40 bg-card text-card-foreground rounded-xl shadow-lg border border-border p-4 w-72 md:w-80"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <button 
              onClick={() => {
                setShowWelcomeBubble(false);
                localStorage.setItem(QOPILOT_WELCOMED_KEY, 'true');
              }}
              className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss welcome message"
            >
              <X size={18} />
            </button>
            <div className="flex items-center mb-3">
                 <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center mr-3 shrink-0">
                     <Bot size={22} />
                 </div>
                 <div>
                     <h3 className="font-semibold text-sm">Chat with QoPilot!</h3>
                     <p className="text-xs text-muted-foreground">Your AI study abroad assistant.</p>
                 </div>
             </div>
            <p className="text-xs text-muted-foreground mb-3.5">
              Have questions about courses, universities, or applications? Get instant answers.
            </p>
            <motion.button
              onClick={() => {
                setShowWelcomeBubble(false);
                localStorage.setItem(QOPILOT_WELCOMED_KEY, 'true');
                setIsOpen(true);
              }}
              className="w-full py-2 px-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors shadow-sm"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <MessageCircle size={16} />
              Start Chatting
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen((prev) => !prev)}
        className="fixed bottom-6 right-6 z-50 p-4 bg-blue-600 text-white rounded-full shadow-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300"
        aria-label={isOpen ? 'Close Chat' : 'Open Chat'}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? <ChevronDown size={24} /> : <MessageCircle size={24} />}
      </motion.button>

      {/* Options Menu Portal */}
      {hydrated && showOptionsMenu && optionsMenuButtonRef.current && (
        createPortal(
          <motion.div
            className="fixed inset-0 z-[1000]" 
            onClick={() => setShowOptionsMenu(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute bg-card text-card-foreground rounded-lg shadow-xl border border-border py-1.5 w-48"
              style={{
                top: (optionsMenuButtonRef.current?.getBoundingClientRect().bottom || 0) + 8,
                left: Math.max(10, (optionsMenuButtonRef.current?.getBoundingClientRect().right || 0) - 192), // Adjusted left positioning
              }}
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleClearChat}
                className="flex items-center w-full px-3.5 py-2 text-sm hover:bg-muted dark:hover:bg-gray-700/60 transition-colors"
              >
                <Trash2 size={15} className="mr-2.5 text-muted-foreground" />
                Clear Chat
              </button>
              <button
                onClick={handleViewPastChats}
                className="flex items-center w-full px-3.5 py-2 text-sm hover:bg-muted dark:hover:bg-gray-700/60 transition-colors"
              >
                <Clock size={15} className="mr-2.5 text-muted-foreground" />
                Past Chats
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowOptionsMenu(false);
                }}
                className="flex items-center w-full px-3.5 py-2 text-sm hover:bg-muted dark:hover:bg-gray-700/60 transition-colors"
              >
                <Minimize size={15} className="mr-2.5 text-muted-foreground" />
                Minimize Chat
              </button>
            </motion.div>
          </motion.div>,
          document.body
        )
      )}

      {/* Chat Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-end md:items-end md:justify-end p-0 md:p-4 bg-black/30 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none"
            onClick={() => setIsOpen(false)} 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative flex flex-col w-full h-full md:w-[400px] md:h-[70vh] md:max-h-[700px] mx-auto md:mx-0 bg-card text-card-foreground md:rounded-2xl shadow-2xl overflow-hidden border border-border/40 dark:border-gray-700/50"
              onClick={(e) => e.stopPropagation()} 
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            >
              {/* Clear Chat Confirmation Banner */}
              <AnimatePresence>
                {showClearConfirmation && (
                  <motion.div
                    className="absolute inset-x-0 top-0 z-[150] py-2.5 px-4 bg-background dark:bg-gray-800 border-b border-border dark:border-gray-700 flex items-center justify-between shadow-sm"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className="text-sm font-medium">Clear chat history?</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={cancelClearChat}
                        className="px-3 py-1 text-xs hover:bg-muted dark:hover:bg-gray-700 rounded-md transition-colors text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmClearChat}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Past Chats Overlay */} 
              <AnimatePresence>
                {showPastChats && (
                  <motion.div
                    className="absolute inset-0 z-[50] bg-background/95 dark:bg-gray-800/95 backdrop-blur-sm flex flex-col"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="flex items-center justify-between p-4 border-b border-border dark:border-gray-700">
                      <h3 className="text-lg font-semibold">Past Conversations</h3>
                      <button 
                        onClick={() => setShowPastChats(false)} 
                        className="p-1.5 text-muted-foreground hover:text-foreground rounded-full hover:bg-muted dark:hover:bg-gray-700"
                        aria-label="Close past chats view"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      {pastChats.length > 0 ? (
                        <div className="space-y-3">
                          {pastChats.map((chat) => (
                            <div 
                              key={chat.id}
                              onClick={() => loadSavedChat(chat.id)} 
                              className="p-3 rounded-lg border border-border/50 dark:border-gray-700/50 hover:border-primary/30 dark:hover:border-primary/70 hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer transition-colors relative group"
                            >
                              <div className="flex justify-between items-start mb-1">
                                <p className="text-xs text-muted-foreground">{chat.date}</p>
                                <button 
                                  onClick={(e) => deleteSavedChat(chat.id, e)} 
                                  className="absolute top-1.5 right-1.5 p-1 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-full hover:bg-destructive/10 dark:hover:bg-red-500/20"
                                  aria-label="Delete chat"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <p className="text-sm font-medium line-clamp-1 pr-6">{chat.preview}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground p-6">
                          <Clock size={36} className="mb-3 opacity-60" />
                          <p className="font-medium">No Past Conversations</p>
                          <p className="text-xs">Chats will be saved here automatically when you close them or start a new one.</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-blue-600/30 dark:border-blue-500/40 bg-blue-500 dark:bg-gradient-to-b dark:from-blue-600 dark:to-blue-700 text-white shadow-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-white/15 dark:bg-white/10 flex items-center justify-center shadow-inner overflow-hidden shrink-0">
                    <img 
                      src="/qopilot-avatar.png" 
                      alt="QoPilot Avatar" 
                      width={32} height={32}
                      className="object-contain p-0.5"
                    />
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight text-white">QoPilot</h3>
                </div>
                <div className="flex items-center gap-1.5">
                    <motion.button
                        onClick={() => setIsOpen(false)} 
                        className="md:hidden p-2 rounded-full text-white/80 hover:bg-blue-400/30 focus:outline-none focus:ring-1 focus:ring-white/50 transition-colors"
                        aria-label="Close chat"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <X size={20} />
                    </motion.button>
                    <button 
                        ref={optionsMenuButtonRef}
                        onClick={() => setShowOptionsMenu(prev => !prev)} 
                        className="p-2 rounded-full text-white/80 hover:bg-blue-400/30 hover:text-white focus:outline-none focus:ring-1 focus:ring-white/50 transition-colors"
                        aria-label="Chat options"
                    >
                        <MoreVertical size={20} /> 
                    </button>
                </div>
              </div>

              {/* Messages Area */} 
              <div 
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-background/60 dark:bg-gray-900/70 backdrop-blur-sm"
              >
                {/* Welcome Image */} 
                <AnimatePresence>
                  {showWelcomeImage && messages.length <= 1 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      className="flex flex-col items-center justify-center pt-8 pb-4 text-center"
                    >
                      <img 
                        src="/qopilot-avatar.png" 
                        alt="QoPilot Welcome" 
                        width={100} height={100}
                        className="object-contain rounded-full shadow-lg mb-4"
                      />
                       <p className="text-sm font-medium text-muted-foreground dark:text-gray-400">Ask me anything about studying abroad!</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Actual Messages - USE ChatMessage COMPONENT */} 
                {messages.map((msg, index) => (
                  <ChatMessage 
                    key={index} 
                    msg={msg} 
                    handleContactRedirect={handleContactRedirect} 
                  />
                ))}
                
                {/* Suggested Questions */} 
                <AnimatePresence>
                 {messages.length === 1 && messages[0]?.role === 'bot' && !isLoading && currentFollowUps === initialStaticSuggestions && (
                     <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                      className="pt-2 pb-1"
                    >
                      <div className="text-xs font-medium text-muted-foreground dark:text-gray-400 mb-2 pl-2">You can ask me:</div>
                      <div className="flex flex-col w-full">
                        {currentFollowUps.map((question, i) => (
                          <SuggestionButton 
                            key={`initial-sugg-${i}`}
                            text={question} 
                            onClick={() => handleSuggestionClick(question)}
                            disabled={isLoading}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                  {messages.length > 1 && messages[messages.length - 1].role === 'bot' && !isLoading && currentFollowUps.length > 0 && currentFollowUps !== initialStaticSuggestions && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                        className="mt-1 mb-3 pl-11" 
                      >
                        <div className="flex flex-col w-full">
                          {currentFollowUps.map((question, i) => (
                            <SuggestionButton 
                              key={`followup-sugg-${i}`}
                              text={question} 
                              onClick={() => handleSuggestionClick(question)}
                              disabled={isLoading}
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                </AnimatePresence>
                
                {/* Typing Indicator */} 
                <AnimatePresence>
                 {isLoading && (
                  <motion.div 
                    className="flex items-end gap-2 justify-start"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="w-9 h-9 rounded-full bg-muted dark:bg-gray-700 flex items-center justify-center shrink-0 overflow-hidden self-start mt-1"> 
                       <img 
                         src="/qopilot-avatar.png" 
                         alt="QoPilot Avatar" 
                         width={28} height={28}
                         className="object-contain p-0.5"
                       />
                     </div>
                    <div className="px-4 py-3.5 rounded-2xl bg-muted/70 dark:bg-gray-700/80 border border-border/20 dark:border-gray-600/50 flex items-center space-x-1.5 shadow-sm">
                      <motion.div className="w-2 h-2 bg-muted-foreground/70 rounded-full" animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 1, delay: 0 }} />
                      <motion.div className="w-2 h-2 bg-muted-foreground/70 rounded-full" animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 1, delay: 0.2 }} />
                      <motion.div className="w-2 h-2 bg-muted-foreground/70 rounded-full" animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 1, delay: 0.4 }} />
                    </div>
                  </motion.div>
                 )}
                </AnimatePresence>
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */} 
              <form onSubmit={handleSubmit} className="relative p-3 border-t border-border dark:border-gray-700/60 bg-background dark:bg-gray-800/50">
                <div className="flex items-center relative">
                  <input
                    type="text"
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit(e)}
                    placeholder="Ask QoPilot..."
                    className="w-full pl-4 pr-12 py-3 text-sm bg-background dark:bg-gray-700/50 text-foreground dark:text-gray-100 border border-border dark:border-gray-600/80 rounded-full focus:outline-none focus:border-primary dark:focus:border-blue-500 focus:ring-1 focus:ring-primary dark:focus:ring-blue-500 transition-all disabled:opacity-60 placeholder-muted-foreground dark:placeholder-gray-400 shadow-sm"
                    disabled={isLoading}
                    aria-label="Chat input"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !input.trim()}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2.5 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 disabled:bg-primary/70 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/80 focus:ring-offset-1 transition-colors shadow-sm hover:shadow-md active:shadow-inner"
                    aria-label="Send message"
                  >
                    {isLoading ? <Loader2 size={20} className="animate-spin" /> : <SendHorizonal size={20} />}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
